// Catálogo de widgets del Inicio personalizable (2026-11-01).
//
// Cada entrada: tipo (clave estable que se guarda en el backend), nombre y
// descripción para la galería, categoría, tamaño por defecto y mínimo en
// celdas de la rejilla (12 columnas), opciones configurables y el componente.
// Todos SOLO LEEN lo que ya trae el panel (dashboard del último ejercicio,
// comparativa, consolidado de proyección y salud): no hay cálculos nuevos más
// allá de variaciones y repartos visuales. Proyectado siempre en amarillo.
import {
  AreaChart, Area, BarChart, Bar, Cell, PieChart as RPieChart, Pie, XAxis, YAxis,
  Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine,
} from "recharts";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowDownRight, Building2, Upload, HeartPulse, Scale, FileText,
  ArrowRightLeft, Sigma, TrendingUp, Wallet, Download, BookOpen, GitCompare,
  PieChart, Gauge, Table2, BarChart3, StickyNote, Layers, Landmark, AlertTriangle,
  CalendarRange, Rocket, Activity, Percent, Droplets,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CHART, CHART_SERIE, axisProps, ChartTooltip, ChartGradient, fmtCompact } from "@/components/ui/chart-kit";
import {
  METRICAS, FORMATOS, OPCIONES_METRICA, OPCIONES_METRICA_EUR, serieMetrica, esNum, nf0, nf1, nf2,
  Contador, Marco, BarrasPildora, Anillo, Barra, SEMAFORO,
} from "./widgetKit";

const OPC_PCT = OPCIONES_METRICA.filter((o) => METRICAS[o.v].u === "pct");
const fmtDe = (clave) => FORMATOS[METRICAS[clave]?.u || "eur"];
const ultimoReal = (s) => [...s].reverse().find((x) => !x.proy && esNum(x.valor));
const variacion = (a, b) => (esNum(a) && esNum(b) && b !== 0 ? ((a - b) / Math.abs(b)) * 100 : null);

// ─────────────────────────────── Cifras ───────────────────────────────

function WKpi({ d, config }) {
  const clave = METRICAS[config.metrica] ? config.metrica : "ventas";
  const m = METRICAS[clave];
  const f = FORMATOS[m.u];
  const serie = serieMetrica(clave, d);
  const reales = serie.filter((s) => !s.proy);
  const actual = reales[reales.length - 1]?.valor;
  const previo = reales[reales.length - 2]?.valor;
  // Variación: % para importes, puntos para ratios/porcentajes
  const delta = m.u === "eur" ? variacion(actual, previo) : esNum(actual) && esNum(previo) ? actual - previo : null;
  const bueno = esNum(delta) && (m.mejor === "bajo" ? delta < 0 : delta > 0);
  const datos = serie.map((s) => ({ anio: String(s.anio), valor: s.valor, proy: s.proy }));
  const primerProy = serie.find((s) => s.proy)?.anio;
  return (
    <Marco to={`${d.base}/comparativa`} titulo={m.l} subtitulo={`Ejercicio ${reales[reales.length - 1]?.anio ?? d.ultimo.anio}`} icono={Activity}>
      <div className="flex items-end justify-between gap-2">
        <Contador valor={actual} formato={f.corto} className="text-[1.7rem] font-light leading-none tracking-tight tabular-nums" />
        {esNum(delta) && (
          <span className={cn("inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] tabular-nums",
            bueno ? "bg-emerald-500/15 text-emerald-700" : "bg-rose-500/15 text-rose-700")}
          title={`Frente a ${reales[reales.length - 2]?.anio}`}>
            {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {m.u === "eur" ? `${nf1.format(Math.abs(delta))} %` : nf2.format(Math.abs(delta))}
          </span>
        )}
      </div>
      <div className="mt-1 min-h-[28px] flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={datos} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs><ChartGradient id={`kpi-${clave}`} color={CHART.mostaza} from={0.35} /></defs>
            {primerProy && <ReferenceArea x1={String(primerProy)} x2={datos[datos.length - 1].anio} fill="hsl(var(--proy))" fillOpacity={0.1} />}
            <Tooltip content={<ChartTooltip formatter={(v) => f.largo(v)} />} />
            <Area type="monotone" dataKey="valor" name={m.l} stroke={CHART.mostaza} strokeWidth={2} fill={`url(#kpi-${clave})`} dot={false} connectNulls />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Marco>
  );
}

function WComparaAnios({ d }) {
  const filas = ["ventas", "ebitda", "resultado_neto", "cash_flow"].map((k) => {
    const s = serieMetrica(k, d, false);
    const act = s[s.length - 1];
    const ant = s[s.length - 2];
    return { k, m: METRICAS[k], act, ant, delta: variacion(act?.valor, ant?.valor) };
  });
  const a1 = filas[0].act?.anio;
  const a0 = filas[0].ant?.anio;
  return (
    <Marco to={`${d.base}/comparativa-anios`} titulo="Año contra año" subtitulo={a0 ? `${a1} frente a ${a0}` : "Hace falta más de un ejercicio"} icono={CalendarRange}>
      <div className="flex flex-1 flex-col justify-center divide-y divide-border/50">
        {filas.map((f) => (
          <div key={f.k} className="flex items-center justify-between gap-2 py-1.5 text-[12px]">
            <span className="truncate text-muted-foreground">{f.m.l}</span>
            <span className="ml-auto tabular-nums">{FORMATOS.eur.corto(f.act?.valor)}</span>
            <span className={cn("w-16 text-right tabular-nums", !esNum(f.delta) ? "text-muted-foreground" : f.delta >= 0 ? "text-emerald-600" : "text-rose-600")}>
              {esNum(f.delta) ? `${f.delta >= 0 ? "+" : "−"}${nf1.format(Math.abs(f.delta))} %` : "—"}
            </span>
          </div>
        ))}
      </div>
    </Marco>
  );
}

const FILAS_TABLA = {
  pyg: ["ventas", "ebitda", "margen_ebitda", "ebit", "resultado_neto"],
  balance: ["patrimonio_neto", "fondo_maniobra", "nof", "tesoreria", "dfn"],
  ratios: ["liquidez", "solvencia", "dfn_ebitda", "roe", "plazo_cobro"],
};

function WTablaAnios({ d, config }) {
  const filas = FILAS_TABLA[config.filas] || FILAS_TABLA.pyg;
  const series = filas.map((k) => ({ k, m: METRICAS[k], s: serieMetrica(k, d) }));
  const anios = series[0].s.map((x) => ({ anio: x.anio, proy: x.proy }));
  // Columnas de proyección solo si alguna fila tiene dato (N/D no rellena columnas)
  const cols = anios.filter((a) => !a.proy || series.some((r) => esNum(r.s.find((x) => x.anio === a.anio)?.valor)));
  return (
    <Marco to={`${d.base}/comparativa`} titulo="Tabla por años" subtitulo={{ pyg: "Cuenta de resultados", balance: "Balance y circulante", ratios: "Ratios clave" }[config.filas] || "Cuenta de resultados"} icono={Table2}>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full table-fixed text-[11px] tabular-nums">
          <colgroup><col className="w-[34%]" />{cols.map((a) => <col key={a.anio} />)}</colgroup>
          <thead>
            <tr className="text-muted-foreground">
              <th className="py-1 text-left font-medium" />
              {cols.map((a) => (
                <th key={a.anio} className={cn("py-1 text-right font-medium", a.proy && "bg-proy text-proy")}>{a.anio}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {series.map((r) => (
              <tr key={r.k} className="border-t border-border/40">
                <td className="truncate py-1 pr-1 text-left">{r.m.l}</td>
                {cols.map((a) => {
                  const v = r.s.find((x) => x.anio === a.anio)?.valor;
                  return <td key={a.anio} className={cn("py-1 text-right", a.proy && "bg-proy text-proy")}>{FORMATOS[r.m.u].corto(v)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Marco>
  );
}

function WFicha({ d }) {
  const { empresa, ultimo, ejercicios, dash } = d;
  const p = dash?.periodo;
  return (
    <Marco to={d.base} titulo={empresa.nombre} subtitulo="Ficha de la empresa" icono={Building2} oscuro>
      <dl className="grid flex-1 grid-cols-2 content-center gap-x-3 gap-y-2 text-[12px]">
        {[
          ["Ejercicios", nf0.format(ejercicios.length)],
          ["Último", `${ultimo.anio}${p?.parcial ? ` · ${p.dias} días` : ""}`],
          ["Desde", ejercicios.length ? String(ejercicios[ejercicios.length - 1].anio) : "—"],
          ["Tu acceso", { owner: "Propietario", write: "Edición", read: "Lectura" }[empresa.my_role || "owner"]],
        ].map(([k, v]) => (
          <div key={k}>
            <dt className="text-[10px] uppercase tracking-wide text-white/50">{k}</dt>
            <dd className="truncate text-white">{v}</dd>
          </div>
        ))}
      </dl>
    </Marco>
  );
}

// ─────────────────────────────── Gráficas ───────────────────────────────

function WEvolucion({ d, config }) {
  const a = METRICAS[config.serie_a] ? config.serie_a : "ventas";
  const b = METRICAS[config.serie_b] ? config.serie_b : "ebitda";
  const sa = serieMetrica(a, d);
  const sb = serieMetrica(b, d);
  const anios = [...new Set([...sa, ...sb].map((x) => x.anio))].sort((x, y) => x - y);
  const proyAnios = new Set([...sa, ...sb].filter((x) => x.proy).map((x) => x.anio));
  const datos = anios.map((y) => ({
    anio: String(y),
    a: sa.find((x) => x.anio === y)?.valor ?? null,
    b: sb.find((x) => x.anio === y)?.valor ?? null,
  }));
  const primer = anios.find((y) => proyAnios.has(y));
  const fa = fmtDe(a);
  const fb = fmtDe(b);
  return (
    <Marco to={`${d.base}/comparativa`} titulo={`${METRICAS[a].l} y ${METRICAS[b].l}`} subtitulo={primer ? "Histórico y proyección" : "Histórico"} icono={TrendingUp}>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={datos} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
            <defs>
              <ChartGradient id={`evo-a-${a}`} color={CHART.gunmetal} from={0.22} />
              <ChartGradient id={`evo-b-${b}`} color={CHART.mostaza} from={0.4} />
            </defs>
            {primer && (
              <ReferenceArea x1={String(primer)} x2={String(anios[anios.length - 1])} fill="hsl(var(--proy))" fillOpacity={0.07}
                label={{ value: "Proyección", position: "insideTopRight", fontSize: 10, fill: "hsl(var(--proy-foreground))" }} />
            )}
            <XAxis dataKey="anio" {...axisProps} />
            <YAxis tickFormatter={fa.eje} {...axisProps} width={48} />
            <Tooltip content={<ChartTooltip formatter={(v, n) => (n === METRICAS[a].l ? fa.largo(v) : fb.largo(v))} />} />
            <Area type="monotone" dataKey="a" name={METRICAS[a].l} stroke={CHART.gunmetal} strokeWidth={2.25} fill={`url(#evo-a-${a})`} dot={false} connectNulls />
            <Area type="monotone" dataKey="b" name={METRICAS[b].l} stroke={CHART.mostaza} strokeWidth={2.25} fill={`url(#evo-b-${b})`} dot={false} connectNulls />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Marco>
  );
}

function WBarras({ d, config }) {
  const clave = METRICAS[config.metrica] ? config.metrica : "ventas";
  const serie = serieMetrica(clave, d);
  const f = fmtDe(clave);
  return (
    <Marco to={`${d.base}/comparativa-anios`} titulo={`${METRICAS[clave].l} por año`} subtitulo={`${d.ultimo.anio} destacado`} icono={BarChart3}>
      <BarrasPildora datos={serie} destacado={d.ultimo.anio} formato={f.eje} />
    </Marco>
  );
}

function WCrecimiento({ d, config }) {
  const clave = METRICAS[config.metrica] ? config.metrica : "ventas";
  const serie = serieMetrica(clave, d);
  const datos = serie.slice(1).map((s, i) => ({ anio: String(s.anio), valor: variacion(s.valor, serie[i].valor), proy: s.proy }));
  return (
    <Marco to={`${d.base}/comparativa`} titulo={`Crecimiento de ${METRICAS[clave].l.toLowerCase()}`} subtitulo="Variación frente al año anterior" icono={Percent}>
      <div className="min-h-0 flex-1">
        {datos.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datos} margin={{ top: 6, right: 4, left: -18, bottom: 0 }}>
              <XAxis dataKey="anio" {...axisProps} />
              <YAxis tickFormatter={(v) => `${nf0.format(v)}%`} {...axisProps} width={44} />
              <ReferenceLine y={0} stroke={CHART.borde} />
              <Tooltip cursor={{ fill: CHART.muted, fillOpacity: 0.08 }} content={<ChartTooltip formatter={(v) => (esNum(v) ? `${nf1.format(v)} %` : "N/D")} />} />
              <Bar dataKey="valor" name="Variación" radius={[6, 6, 6, 6]} maxBarSize={26}>
                {datos.map((x) => (
                  <Cell key={x.anio} fill={x.proy ? "hsl(var(--proy))" : x.valor >= 0 ? CHART.profit : CHART.loss} fillOpacity={x.proy ? 0.55 : 0.9} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <Vacio texto="Hace falta más de un año" />}
      </div>
    </Marco>
  );
}

const GASTOS = [
  { k: "aprovisionamientos", l: "Aprovisionamientos" },
  { k: "gastos_personal", l: "Personal" },
  { k: "otros_gastos", l: "Otros gastos" },
  { k: "amortizaciones", l: "Amortizaciones" },
  { k: "gastos_financieros", l: "Financieros" },
];

function WGastos({ d }) {
  const pyg = d.dash?.pyg_resumen || {};
  const datos = GASTOS.map((g) => ({ name: g.l, value: Math.abs(pyg[g.k] || 0) })).filter((g) => g.value > 0);
  const total = datos.reduce((s, g) => s + g.value, 0);
  return (
    <Marco to={`/ejercicios/${d.ultimo.id}/analisis?tab=pyg`} titulo="Reparto de gastos" subtitulo={`Ejercicio ${d.ultimo.anio}`} icono={PieChart}>
      {total ? (
        <div className="flex min-h-0 flex-1 items-center gap-3">
          <div className="h-full min-h-0 w-[45%]">
            <ResponsiveContainer width="100%" height="100%">
              <RPieChart>
                <Pie data={datos} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="92%" paddingAngle={2} stroke="none">
                  {datos.map((g, i) => <Cell key={g.name} fill={CHART_SERIE[i % CHART_SERIE.length]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip formatter={(v) => FORMATOS.eur.largo(v)} />} />
              </RPieChart>
            </ResponsiveContainer>
          </div>
          <ul className="min-w-0 flex-1 space-y-1.5 text-[11px]">
            {datos.map((g, i) => (
              <li key={g.name} className="flex items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: CHART_SERIE[i % CHART_SERIE.length] }} />
                <span className="truncate text-muted-foreground">{g.name}</span>
                <span className="ml-auto tabular-nums">{nf0.format((g.value / total) * 100)} %</span>
              </li>
            ))}
          </ul>
        </div>
      ) : <Vacio texto="Sin gastos en el ejercicio" />}
    </Marco>
  );
}

function WCascada({ d }) {
  const p = d.dash?.pyg_resumen || {};
  const ventas = p.ventas || 0;
  const pasos = [
    { l: "Ventas", v: p.ventas, total: true },
    { l: "Aprovisionamientos", v: p.aprovisionamientos },
    { l: "Personal", v: p.gastos_personal },
    { l: "Otros gastos", v: p.otros_gastos },
    { l: "EBITDA", v: p.ebitda, total: true, dest: true },
    { l: "Amortizaciones", v: p.amortizaciones },
    { l: "EBIT", v: p.resultado_explotacion, total: true },
    { l: "Resultado neto", v: p.resultado_ejercicio, total: true },
  ];
  return (
    <Marco to={`/ejercicios/${d.ultimo.id}/analisis?tab=pyg`} titulo="De ventas a beneficio" subtitulo={`Ejercicio ${d.ultimo.anio} · % sobre ventas`} icono={Layers}>
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-1">
        {pasos.map((s, i) => {
          const pct = ventas ? (Math.abs(s.v || 0) / Math.abs(ventas)) * 100 : 0;
          return (
            <div key={s.l} className="grid grid-cols-[38%_1fr_52px] items-center gap-2 text-[11px]">
              <span className={cn("truncate", s.total ? "font-medium" : "pl-2 text-muted-foreground")}>{s.l}</span>
              <Barra pct={pct} delay={0.15 + i * 0.05} className={s.dest ? "bg-primary" : s.total ? "" : "bg-foreground/25"} />
              <span className="text-right tabular-nums">{FORMATOS.eur.corto(s.v)}</span>
            </div>
          );
        })}
      </div>
    </Marco>
  );
}

function WBalance({ d }) {
  const b = d.dash?.balance_resumen || {};
  const total = b.activo_total || 0;
  const cols = [
    { l: "Activo", partes: [
      { l: "No corriente", v: b.activo_no_corriente, c: CHART.gunmetal },
      { l: "Corriente", v: b.activo_corriente, c: CHART.mostaza },
    ] },
    { l: "PN + Pasivo", partes: [
      { l: "Patrimonio neto", v: b.patrimonio_neto, c: CHART.bronce },
      { l: "Pasivo no corriente", v: b.pasivo_no_corriente, c: CHART.topaz },
      { l: "Pasivo corriente", v: b.pasivo_corriente, c: CHART.pizarra },
    ] },
  ];
  return (
    <Marco to={`/ejercicios/${d.ultimo.id}/analisis?tab=balance`} titulo="Estructura del balance" subtitulo={`Total ${FORMATOS.eur.corto(total)}`} icono={Scale}>
      {total ? (
        <div className="flex min-h-0 flex-1 gap-4">
          {cols.map((col) => (
            <div key={col.l} className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl">
                {col.partes.map((p) => {
                  const pct = Math.max(0, ((p.v || 0) / total) * 100);
                  return pct > 0 ? (
                    <div key={p.l} className="flex items-center justify-center px-1 text-[10px] font-medium text-white" style={{ flexGrow: pct, background: p.c }} title={`${p.l}: ${FORMATOS.eur.largo(p.v)}`}>
                      <span className="truncate">{pct >= 12 ? `${p.l} ${nf0.format(pct)}%` : ""}</span>
                    </div>
                  ) : null;
                })}
              </div>
              <div className="text-center text-[11px] text-muted-foreground">{col.l}</div>
            </div>
          ))}
        </div>
      ) : <Vacio texto="Sin balance" />}
    </Marco>
  );
}

// ─────────────────────────────── Salud y riesgo ───────────────────────────────

function WSalud({ d }) {
  const r = d.salud?.resumen || {};
  const total = (r.ok || 0) + (r.warning || 0) + (r.error || 0);
  const pct = total ? ((r.ok || 0) / total) * 100 : null;
  return (
    <Marco to={`${d.base}/salud`} titulo="Salud de la empresa" subtitulo={total ? `${r.ok || 0} de ${total} comprobaciones en verde` : "Sin datos"} icono={HeartPulse} oscuro>
      <div className="flex min-h-0 flex-1 items-center gap-3">
        <div className="h-full min-h-0 w-[45%]"><Anillo pct={pct} etiqueta="en verde" oscuro /></div>
        <ul className="min-w-0 flex-1 space-y-2">
          {(d.salud?.areas || []).slice(0, 5).map((a) => (
            <li key={a.titulo} className="flex items-center gap-2 text-[12px] text-white/85">
              <span className={cn("h-2 w-2 shrink-0 rounded-full", SEMAFORO[a.estado] || "bg-white/30")} />
              <span className="truncate">{a.titulo}</span>
            </li>
          ))}
        </ul>
      </div>
    </Marco>
  );
}

function WAvisos({ d }) {
  const items = (d.salud?.areas || []).flatMap((a) => (a.items || []).filter((i) => i.estado === "error" || i.estado === "warning").map((i) => ({ ...i, area: a.titulo })));
  items.sort((x, y) => (x.estado === "error" ? -1 : 1) - (y.estado === "error" ? -1 : 1));
  return (
    <Marco to={`${d.base}/salud`} titulo="Avisos a revisar" subtitulo={items.length ? `${items.length} pendientes` : "Nada pendiente"} icono={AlertTriangle}>
      {items.length ? (
        <ul className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-1">
          {items.map((i, n) => (
            <li key={`${i.area}-${i.titulo}-${n}`} className="flex items-start gap-2 text-[12px]">
              <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", SEMAFORO[i.estado])} />
              <span className="min-w-0">
                <span className="block truncate">{i.titulo}</span>
                <span className="block truncate text-[10px] text-muted-foreground">{i.area}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : <Vacio texto="Todas las comprobaciones en verde" ok />}
    </Marco>
  );
}

function WAnillo({ d, config }) {
  const clave = METRICAS[config.metrica] && METRICAS[config.metrica].u === "pct" ? config.metrica : "margen_ebitda";
  const actual = ultimoReal(serieMetrica(clave, d, false));
  const k = d.dash?.kpis || {};
  return (
    <Marco to={`/ejercicios/${d.ultimo.id}/analisis?tab=ebitda`} titulo={METRICAS[clave].l} subtitulo={`Ejercicio ${actual?.anio ?? d.ultimo.anio}`} icono={Gauge}>
      <div className="min-h-0 flex-1"><Anillo pct={actual?.valor} etiqueta={clave.startsWith("ro") ? "rentabilidad" : "sobre ventas"} /></div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>EBIT {FORMATOS.pct.corto(k.margen_ebit)}</span>
        <span>Neto {FORMATOS.pct.corto(k.margen_neto)}</span>
      </div>
    </Marco>
  );
}

function WDeuda({ d }) {
  const dfnE = d.dash?.ratios_endeudamiento?.dfn_ebitda;
  const liq = d.dash?.ratios_liquidez?.liquidez_principal;
  const color = !esNum(dfnE) ? "text-muted-foreground" : dfnE > 4 ? "text-rose-500" : dfnE > 3 ? "text-amber-500" : "text-emerald-500";
  return (
    <Marco to={`${d.base}/comparativa?vista=ratios&bloque=endeudamiento`} titulo="Deuda y liquidez" subtitulo={`Ejercicio ${d.ultimo.anio}`} icono={Scale}>
      <div className="flex flex-1 flex-col justify-center gap-3">
        <div>
          <div className="text-[11px] text-muted-foreground">DFN / EBITDA</div>
          <Contador valor={dfnE} formato={(x) => `${nf2.format(x)}x`} className={cn("text-[2rem] font-light leading-none tabular-nums", color)} />
        </div>
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-muted-foreground">
            <span>Liquidez corriente</span>
            <span className="tabular-nums text-foreground">{esNum(liq) ? nf2.format(liq) : "N/D"}</span>
          </div>
          <Barra pct={((liq || 0) / 2) * 100} />
          <div className="mt-1 text-[10px] text-muted-foreground">Referencia: 1,5 – 2,0</div>
        </div>
      </div>
    </Marco>
  );
}

// Umbrales orientativos (mismos que los semáforos de Salud/Comparativa)
const RATIOS_SEMAFORO = [
  { k: "liquidez", estado: (v) => (v >= 1.5 ? "ok" : v >= 1 ? "warning" : "error") },
  { k: "solvencia", estado: (v) => (v >= 1.5 ? "ok" : v >= 1.1 ? "warning" : "error") },
  { k: "dfn_ebitda", estado: (v) => (v <= 3 ? "ok" : v <= 4 ? "warning" : "error") },
  { k: "roe", estado: (v) => (v >= 8 ? "ok" : v >= 0 ? "warning" : "error") },
  { k: "plazo_cobro", estado: (v) => (v <= 60 ? "ok" : v <= 90 ? "warning" : "error") },
];

function WSemaforo({ d }) {
  const fila = d.comp[d.comp.length - 1];
  return (
    <Marco to={`${d.base}/comparativa?vista=ratios`} titulo="Semáforo de ratios" subtitulo={fila ? `Ejercicio ${fila.anio} · referencias orientativas` : "Sin datos"} icono={Activity}>
      <ul className="flex flex-1 flex-col justify-center gap-1.5">
        {RATIOS_SEMAFORO.map((r) => {
          const m = METRICAS[r.k];
          const v = fila ? m.hist(fila) : null;
          return (
            <li key={r.k} className="flex items-center gap-2 text-[12px]">
              <span className={cn("h-2 w-2 shrink-0 rounded-full", esNum(v) ? SEMAFORO[r.estado(v)] : "bg-foreground/20")} />
              <span className="truncate text-muted-foreground">{m.l}</span>
              <span className="ml-auto tabular-nums">{FORMATOS[m.u].corto(v)}</span>
            </li>
          );
        })}
      </ul>
    </Marco>
  );
}

function WCiclo({ d }) {
  const r = d.dash?.ratios_rotacion || {};
  const plazos = [{ k: "Cobro", v: r.plazo_cobro }, { k: "Pago", v: r.plazo_pago }, { k: "Existencias", v: r.plazo_existencias }];
  const max = Math.max(...plazos.map((p) => p.v || 0), 1);
  const ciclo = esNum(r.plazo_cobro) && esNum(r.plazo_existencias) && esNum(r.plazo_pago) ? r.plazo_cobro + r.plazo_existencias - r.plazo_pago : null;
  return (
    <Marco to={`${d.base}/proyeccion?tab=nof`} titulo="Ciclo de caja" subtitulo={esNum(ciclo) ? `Ciclo neto ${nf0.format(ciclo)} días` : "Plazos medios en días"} icono={Wallet}>
      <div className="flex flex-1 flex-col justify-center gap-2.5">
        {plazos.map((p, i) => (
          <div key={p.k}>
            <div className="mb-1 flex justify-between text-[11px]">
              <span className="text-muted-foreground">{p.k}</span>
              <span className="tabular-nums">{FORMATOS.dias.corto(p.v)}</span>
            </div>
            <Barra pct={((p.v || 0) / max) * 100} delay={0.3 + i * 0.08} className={i === 0 ? "bg-primary" : ""} />
          </div>
        ))}
      </div>
    </Marco>
  );
}

function WCajaDeuda({ d }) {
  const b = d.dash?.balance_resumen || {};
  const filas = [
    { l: "Tesorería", v: b.tesoreria, c: "bg-emerald-500" },
    { l: "Deuda financiera L/P", v: b.deuda_financiera_lp, c: "" },
    { l: "Deuda financiera C/P", v: b.deuda_financiera_cp, c: "bg-primary" },
  ];
  const max = Math.max(...filas.map((f) => Math.abs(f.v || 0)), 1);
  return (
    <Marco to={`${d.base}/comparativa?vista=ratios&bloque=endeudamiento`} titulo="Caja y deuda" subtitulo={`DFN ${FORMATOS.eur.corto(b.dfn)}`} icono={Landmark}>
      <div className="flex flex-1 flex-col justify-center gap-2.5">
        {filas.map((f, i) => (
          <div key={f.l}>
            <div className="mb-1 flex justify-between text-[11px]">
              <span className="text-muted-foreground">{f.l}</span>
              <span className="tabular-nums">{FORMATOS.eur.corto(f.v)}</span>
            </div>
            <Barra pct={(Math.abs(f.v || 0) / max) * 100} delay={0.3 + i * 0.08} className={f.c} />
          </div>
        ))}
      </div>
    </Marco>
  );
}

function WProyeccion({ d }) {
  const p = d.proy;
  const futuros = (p?.anios_proyectados || []).filter((a) => !d.comp.some((c) => c.anio === a));
  const ult = futuros[futuros.length - 1];
  const vUlt = ult ? p?.kpis?.ventas?.por_anio?.[String(ult)] : null;
  const eUlt = ult ? p?.kpis?.ebitda?.por_anio?.[String(ult)] : null;
  const base = ultimoReal(serieMetrica("ventas", d, false));
  const n = ult && base ? ult - base.anio : 0;
  const cagr = n > 0 && esNum(vUlt) && base?.valor > 0 && vUlt > 0 ? (Math.pow(vUlt / base.valor, 1 / n) - 1) * 100 : null;
  return (
    <Marco to={`${d.base}/proyeccion`} titulo="Proyección" subtitulo={ult ? `${futuros[0]}–${ult}` : "Sin proyección configurada"} icono={Rocket}>
      {ult ? (
        <div className="grid flex-1 grid-cols-3 content-center gap-2">
          {[
            ["Ventas " + ult, FORMATOS.eur.corto(vUlt)],
            ["EBITDA " + ult, FORMATOS.eur.corto(eUlt)],
            ["Crec. anual", esNum(cagr) ? `${nf1.format(cagr)} %` : "N/D"],
          ].map(([k, v]) => (
            <div key={k} className="rounded-2xl bg-proy p-2 text-center">
              <div className="truncate text-[10px] text-muted-foreground">{k}</div>
              <div className="truncate text-[15px] font-medium tabular-nums text-proy">{v}</div>
            </div>
          ))}
        </div>
      ) : <Vacio texto="Configura los drivers para ver el futuro" />}
    </Marco>
  );
}

// ─────────────────────────────── Navegación y otros ───────────────────────────────

function WAccesos({ d }) {
  const { base, ultimo } = d;
  const accesos = [
    { label: "Balance", icon: Scale, to: `/ejercicios/${ultimo.id}/analisis?tab=balance` },
    { label: "PyG", icon: FileText, to: `/ejercicios/${ultimo.id}/analisis?tab=pyg` },
    { label: "Flujos", icon: ArrowRightLeft, to: `/ejercicios/${ultimo.id}/analisis?tab=flujos` },
    { label: "Resumen", icon: Sigma, to: `${base}/resumen` },
    { label: "Comparativa", icon: PieChart, to: `${base}/comparativa` },
    { label: "Años", icon: GitCompare, to: `${base}/comparativa-anios` },
    { label: "Drivers", icon: TrendingUp, to: `${base}/proyeccion` },
    { label: "NOF", icon: Wallet, to: `${base}/proyeccion?tab=nof` },
    { label: "Exportar", icon: Download, to: `${base}/exportaciones` },
    { label: "Gestión", icon: Building2, to: base },
    { label: "Importar", icon: Upload, to: `/ejercicios/${ultimo.id}/importar` },
    { label: "Holded", icon: BookOpen, to: `${base}/holded` },
  ];
  return (
    <Marco titulo="Accesos directos" subtitulo="Todas las partes de la app" icono={ArrowUpRight}>
      <div className="grid min-h-0 flex-1 grid-cols-[repeat(auto-fill,minmax(64px,1fr))] content-center gap-1.5 overflow-hidden">
        {accesos.map((a) => (
          <Link key={a.label} to={a.to}
            className="flex flex-col items-center justify-center gap-1 rounded-xl bg-foreground/[0.04] px-1 py-2 text-[11px] text-muted-foreground transition-colors hover:bg-[hsl(var(--pill-active))] hover:text-[hsl(var(--pill-active-foreground))]">
            <a.icon className="h-3.5 w-3.5" />
            {a.label}
          </Link>
        ))}
      </div>
    </Marco>
  );
}

function WNota({ config }) {
  const oscuro = config.tono === "oscuro";
  return (
    <Marco titulo={config.titulo || "Nota"} icono={StickyNote} oscuro={oscuro}>
      <p className={cn("min-h-0 flex-1 overflow-auto whitespace-pre-wrap text-[13px] leading-relaxed", oscuro ? "text-white/85" : "text-foreground/85")}>
        {config.texto || "Escribe aquí un recordatorio para el equipo (Configurar → Texto)."}
      </p>
    </Marco>
  );
}

function Vacio({ texto, ok }) {
  return (
    <div className="flex flex-1 items-center justify-center text-center text-[12px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        {ok ? <HeartPulse className="h-3.5 w-3.5 text-emerald-500" /> : <Droplets className="h-3.5 w-3.5 opacity-60" />}
        {texto}
      </span>
    </div>
  );
}

// ─────────────────────────────── Catálogo ───────────────────────────────

export const CATEGORIAS = ["Cifras", "Gráficas", "Salud y riesgo", "Navegación y notas"];

const sel = (clave, etiqueta, opciones) => ({ clave, etiqueta, tipo: "select", opciones });

export const CATALOGO = [
  { tipo: "kpi", nombre: "Cifra clave", desc: "Una métrica grande con su variación frente al año anterior y una mini-gráfica histórica + proyectada.", cat: "Cifras", icono: Activity, w: 3, h: 2, minW: 2, minH: 2,
    opciones: [sel("metrica", "Métrica", OPCIONES_METRICA)], defecto: { metrica: "ventas" }, C: WKpi },
  { tipo: "compara_anios", nombre: "Año contra año", desc: "Ventas, EBITDA, resultado y cash flow del último ejercicio frente al anterior.", cat: "Cifras", icono: CalendarRange, w: 3, h: 3, minW: 3, minH: 3, C: WComparaAnios },
  { tipo: "tabla_anios", nombre: "Tabla por años", desc: "Mini-tabla con todos los años (proyectados en amarillo): resultados, balance o ratios.", cat: "Cifras", icono: Table2, w: 6, h: 3, minW: 4, minH: 3,
    opciones: [sel("filas", "Contenido", [{ v: "pyg", l: "Cuenta de resultados" }, { v: "balance", l: "Balance y circulante" }, { v: "ratios", l: "Ratios clave" }])], defecto: { filas: "pyg" }, C: WTablaAnios },
  { tipo: "proyeccion", nombre: "Resumen de la proyección", desc: "Ventas y EBITDA del último año proyectado y crecimiento anual compuesto.", cat: "Cifras", icono: Rocket, w: 4, h: 2, minW: 3, minH: 2, C: WProyeccion },
  { tipo: "ficha", nombre: "Ficha de la empresa", desc: "Ejercicios importados, último periodo y tu nivel de acceso.", cat: "Cifras", icono: Building2, w: 3, h: 2, minW: 2, minH: 2, C: WFicha },

  { tipo: "evolucion", nombre: "Evolución en el tiempo", desc: "Dos métricas en área, histórico y proyección sombreada.", cat: "Gráficas", icono: TrendingUp, w: 6, h: 4, minW: 3, minH: 3,
    opciones: [sel("serie_a", "Serie 1", OPCIONES_METRICA_EUR), sel("serie_b", "Serie 2", OPCIONES_METRICA_EUR)], defecto: { serie_a: "ventas", serie_b: "ebitda" }, C: WEvolucion },
  { tipo: "barras", nombre: "Barras por año", desc: "Una métrica en barras de píldora; el último año en amarillo y los proyectados rayados.", cat: "Gráficas", icono: BarChart3, w: 3, h: 4, minW: 2, minH: 3,
    opciones: [sel("metrica", "Métrica", OPCIONES_METRICA)], defecto: { metrica: "ventas" }, C: WBarras },
  { tipo: "crecimiento", nombre: "Crecimiento interanual", desc: "Variación % de una métrica año a año, en verde o rojo.", cat: "Gráficas", icono: Percent, w: 4, h: 3, minW: 3, minH: 3,
    opciones: [sel("metrica", "Métrica", OPCIONES_METRICA_EUR)], defecto: { metrica: "ventas" }, C: WCrecimiento },
  { tipo: "cascada", nombre: "De ventas a beneficio", desc: "Cómo se van comiendo las ventas hasta el resultado neto.", cat: "Gráficas", icono: Layers, w: 4, h: 4, minW: 3, minH: 4, C: WCascada },
  { tipo: "gastos", nombre: "Reparto de gastos", desc: "Donut con el peso de cada gran partida de gasto.", cat: "Gráficas", icono: PieChart, w: 4, h: 3, minW: 3, minH: 3, C: WGastos },
  { tipo: "balance", nombre: "Estructura del balance", desc: "Activo frente a patrimonio neto y pasivo, en proporción.", cat: "Gráficas", icono: Scale, w: 3, h: 4, minW: 3, minH: 3, C: WBalance },

  { tipo: "salud", nombre: "Salud de la empresa", desc: "Anillo de comprobaciones en verde y semáforo por área.", cat: "Salud y riesgo", icono: HeartPulse, w: 3, h: 4, minW: 3, minH: 3, C: WSalud },
  { tipo: "avisos", nombre: "Avisos a revisar", desc: "Lista de comprobaciones de Salud en rojo o ámbar.", cat: "Salud y riesgo", icono: AlertTriangle, w: 3, h: 3, minW: 3, minH: 2, C: WAvisos },
  { tipo: "anillo", nombre: "Margen o rentabilidad", desc: "Un porcentaje en anillo: margen EBITDA, neto, ROE o ROA.", cat: "Salud y riesgo", icono: Gauge, w: 3, h: 4, minW: 2, minH: 3,
    opciones: [sel("metrica", "Porcentaje", OPC_PCT)], defecto: { metrica: "margen_ebitda" }, C: WAnillo },
  { tipo: "deuda", nombre: "Deuda y liquidez", desc: "DFN/EBITDA con semáforo y liquidez corriente frente a su referencia.", cat: "Salud y riesgo", icono: Scale, w: 3, h: 4, minW: 2, minH: 3, C: WDeuda },
  { tipo: "semaforo", nombre: "Semáforo de ratios", desc: "Liquidez, solvencia, DFN/EBITDA, ROE y plazo de cobro con su color.", cat: "Salud y riesgo", icono: Activity, w: 3, h: 3, minW: 3, minH: 3, C: WSemaforo },
  { tipo: "ciclo_caja", nombre: "Ciclo de caja", desc: "Plazos de cobro, pago y existencias y el ciclo neto.", cat: "Salud y riesgo", icono: Wallet, w: 3, h: 4, minW: 2, minH: 3, C: WCiclo },
  { tipo: "caja_deuda", nombre: "Caja y deuda", desc: "Tesorería frente a deuda financiera a largo y corto plazo.", cat: "Salud y riesgo", icono: Landmark, w: 3, h: 3, minW: 2, minH: 3, C: WCajaDeuda },

  { tipo: "accesos", nombre: "Accesos directos", desc: "Botones a todas las pantallas de la app.", cat: "Navegación y notas", icono: ArrowUpRight, w: 3, h: 4, minW: 2, minH: 3, C: WAccesos },
  { tipo: "nota", nombre: "Nota", desc: "Un texto libre para el equipo o el cliente (lo ven todos los que tienen acceso).", cat: "Navegación y notas", icono: StickyNote, w: 3, h: 2, minW: 2, minH: 2,
    opciones: [
      { clave: "titulo", etiqueta: "Título", tipo: "texto", max: 40 },
      { clave: "texto", etiqueta: "Texto", tipo: "texto", max: 120, largo: true },
      sel("tono", "Estilo", [{ v: "claro", l: "Cristal" }, { v: "oscuro", l: "Oscuro" }]),
    ], defecto: { titulo: "Nota", texto: "", tono: "claro" }, C: WNota },
];

export const POR_TIPO = Object.fromEntries(CATALOGO.map((w) => [w.tipo, w]));

// Panel por defecto (el del rediseño de octubre, ahora en widgets): llena una
// pantalla sin scroll — 10 filas de alto.
export const PANEL_DEFECTO = [
  { id: "kpi-ventas", tipo: "kpi", x: 0, y: 0, w: 3, h: 2, config: { metrica: "ventas" } },
  { id: "kpi-ebitda", tipo: "kpi", x: 3, y: 0, w: 3, h: 2, config: { metrica: "ebitda" } },
  { id: "kpi-neto", tipo: "kpi", x: 6, y: 0, w: 3, h: 2, config: { metrica: "resultado_neto" } },
  { id: "kpi-cf", tipo: "kpi", x: 9, y: 0, w: 3, h: 2, config: { metrica: "cash_flow" } },
  { id: "evolucion", tipo: "evolucion", x: 0, y: 2, w: 6, h: 4, config: { serie_a: "ventas", serie_b: "ebitda" } },
  { id: "barras", tipo: "barras", x: 6, y: 2, w: 3, h: 4, config: { metrica: "ventas" } },
  { id: "salud", tipo: "salud", x: 9, y: 2, w: 3, h: 4, config: {} },
  { id: "anillo", tipo: "anillo", x: 0, y: 6, w: 3, h: 4, config: { metrica: "margen_ebitda" } },
  { id: "deuda", tipo: "deuda", x: 3, y: 6, w: 3, h: 4, config: {} },
  { id: "ciclo", tipo: "ciclo_caja", x: 6, y: 6, w: 3, h: 4, config: {} },
  { id: "accesos", tipo: "accesos", x: 9, y: 6, w: 3, h: 4, config: {} },
];
export const FILAS_PANTALLA = 10;
